import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import './SearchPage.css';
import { useSEO } from '../lib/seo';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SORT_OPTIONS = [
  { value: 'popular',    label: 'Most Popular' },
  { value: 'rating',     label: 'Top Rated' },
  { value: 'price-asc',  label: 'Price: Low → High' },
  { value: 'price-desc', label: 'Price: High → Low' },
  { value: 'new',        label: 'New Arrivals' },
];

const CARE_LEVELS      = ['Very Easy', 'Easy', 'Moderate', 'Advanced'];
const POPULAR_SEARCHES = ['Indoor Plants', 'Low Maintenance', 'Pet Friendly', 'Air Purifying', 'Plants Bundle', 'Plants for Office'];
const PAGE_SIZE        = 20;

/* ── Pagination ── */
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  pages.push(1);
  if (page > 3) pages.push('…');
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p++) pages.push(p);
  if (page < totalPages - 2) pages.push('…');
  if (totalPages > 1) pages.push(totalPages);

  return (
    <nav className="srp__pagination" aria-label="Search result pages">
      <button
        className="srp__page-btn srp__page-btn--nav"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label="Previous page"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="srp__page-ellipsis">…</span>
        ) : (
          <button
            key={p}
            className={`srp__page-btn${p === page ? ' srp__page-btn--active' : ''}`}
            onClick={() => onChange(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        className="srp__page-btn srp__page-btn--nav"
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        aria-label="Next page"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </nav>
  );
}

export default function SearchPage({ addToCart }) {
  useSEO({
    title: 'Search Plants',
    description: "Search Naturiq's full range of premium indoor plants. Filter by category, care level and price to find your perfect plant.",
    url: '/search',
    noindex: true,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState([]);
  const [sort,        setSort]        = useState('popular');
  const [inputVal,    setInputVal]    = useState(() => new URLSearchParams(location.search).get('q') || '');
  const [search,      setSearch]      = useState(() => new URLSearchParams(location.search).get('q') || '');
  const [maxPrice,    setMaxPrice]    = useState(3000);
  const [careLevels,  setCareLevels]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [filterOpen,  setFilterOpen]  = useState(false);
  const [sortOpen,    setSortOpen]    = useState(false);
  const [page,        setPage]        = useState(1);

  const sortRef  = useRef(null);
  const inputRef = useRef(null);
  const hasQuery = search.trim().length > 0;

  // Sync URL ?q= changes (e.g. search from navbar)
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q') || '';
    setInputVal(q);
    setSearch(q);
    setPage(1);
  }, [location.search]);

  // Focus input on mount if empty
  useEffect(() => {
    if (!hasQuery) inputRef.current?.focus();
  }, []); // eslint-disable-line

  // Close sort dropdown on outside click
  useEffect(() => {
    const h = e => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Debounce input → update search state + URL
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = inputVal.trim();
      setSearch(trimmed);
      setPage(1);
      if (trimmed) {
        navigate(`/search?q=${encodeURIComponent(trimmed)}`, { replace: true });
      } else {
        navigate('/search', { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [inputVal]); // eslint-disable-line

  // Reset page when filters/sort change
  useEffect(() => { setPage(1); }, [sort, maxPrice, careLevels]);

  // Fetch products when search/sort/filters change
  const fetchProducts = useCallback(() => {
    if (!search.trim()) { setAllProducts([]); setLoading(false); return; }
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ sort });
    params.set('search', search.trim());
    if (maxPrice < 3000) params.set('maxPrice', maxPrice);

    fetch(`${BASE}/api/products?${params}`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        const normalised = (data.products || []).map(p => ({
          ...p,
          id:         p._id || p.id,
          imageUrl:   (p.images && p.images[0]?.url) || p.image || null,
          _careLevel: p.care?.difficulty,
        }));
        const filtered = careLevels.length > 0
          ? normalised.filter(p => careLevels.includes(p._careLevel))
          : normalised;
        setAllProducts(filtered);
      })
      .catch(err => { console.error(err); setError('Could not load products.'); setAllProducts([]); })
      .finally(() => setLoading(false));
  }, [search, sort, maxPrice, careLevels]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 300);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  const clearInput = () => {
    setInputVal('');
    navigate('/search', { replace: true });
    inputRef.current?.focus();
  };

  const toggleCare = level =>
    setCareLevels(prev => prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]);

  const hasActiveFilters = maxPrice < 3000 || careLevels.length > 0;
  const clearFilters = () => { setMaxPrice(3000); setCareLevels([]); };

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sort)?.label || 'Sort';

  // Pagination
  const totalPages  = Math.max(1, Math.ceil(allProducts.length / PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const pagedProducts = allProducts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const goToPage = p => {
    setPage(p);
    window.scrollTo(0, 0);
  };

  return (
    <main className="srp">

      {/* ── Search Header ── */}
      <div className="srp__header">
        <div className="srp__header-inner">
          <div className="srp__search-bar">
            <svg className="srp__search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref={inputRef}
              className="srp__search-input"
              type="text"
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              placeholder="Search plants, pots, care tips…"
              autoComplete="off"
              spellCheck="false"
            />
            {inputVal && (
              <button className="srp__search-clear" onClick={clearInput} aria-label="Clear">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          {/* Result count / popular searches */}
          {hasQuery ? (
            <div className="srp__meta">
              {loading ? (
                <span className="srp__meta-loading">Searching…</span>
              ) : allProducts.length > 0 ? (
                <span className="srp__meta-count">
                  <strong>{allProducts.length}</strong> result{allProducts.length !== 1 ? 's' : ''} for <em>"{search}"</em>
                </span>
              ) : null}
            </div>
          ) : (
            <div className="srp__popular">
              <span className="srp__popular-label">Popular:</span>
              {POPULAR_SEARCHES.map(t => (
                <button key={t} className="srp__popular-tag" onClick={() => setInputVal(t)}>{t}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Empty State (no query) ── */}
      {!hasQuery && (
        <div className="srp__idle">
          <div className="srp__idle-icon">🌿</div>
          <h2 className="srp__idle-title">Find your perfect plant</h2>
          <p className="srp__idle-sub">Search by name, care level, light needs, or any keyword</p>
        </div>
      )}

      {/* ── Results Area ── */}
      {hasQuery && (
        <div className="srp__body">

          {/* Toolbar */}
          <div className="srp__toolbar">
            <div className="srp__toolbar-left">
              <button
                className={`srp__filter-btn${filterOpen ? ' srp__filter-btn--active' : ''}`}
                onClick={() => setFilterOpen(o => !o)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <line x1="4" y1="6" x2="20" y2="6"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="11" y1="18" x2="13" y2="18"/>
                </svg>
                Filters
                {hasActiveFilters && <span className="srp__filter-dot" />}
              </button>

              {hasActiveFilters && (
                <div className="srp__chips">
                  {maxPrice < 3000 && (
                    <span className="srp__chip">
                      Under ₹{maxPrice.toLocaleString()}
                      <button onClick={() => setMaxPrice(3000)}>×</button>
                    </span>
                  )}
                  {careLevels.map(l => (
                    <span key={l} className="srp__chip">
                      {l}
                      <button onClick={() => toggleCare(l)}>×</button>
                    </span>
                  ))}
                  <button className="srp__chip-clear" onClick={clearFilters}>Clear</button>
                </div>
              )}
            </div>

            <div className="srp__sort-wrap" ref={sortRef}>
              <button className="srp__sort-btn" onClick={() => setSortOpen(o => !o)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M3 6h18M7 12h10M11 18h2"/>
                </svg>
                {currentSortLabel}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  style={{ transform: sortOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {sortOpen && (
                <div className="srp__sort-dropdown">
                  {SORT_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      className={`srp__sort-option${sort === o.value ? ' srp__sort-option--active' : ''}`}
                      onClick={() => { setSort(o.value); setSortOpen(false); }}
                    >
                      {o.label}
                      {sort === o.value && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Expandable filter panel */}
          {filterOpen && (
            <div className="srp__filter-panel">
              <div className="srp__fp-inner">
                <div className="srp__fp-group">
                  <h4 className="srp__fp-title">Max Price <span>₹{maxPrice.toLocaleString()}</span></h4>
                  <input
                    type="range" min="300" max="3000" step="100"
                    value={maxPrice}
                    onChange={e => setMaxPrice(Number(e.target.value))}
                    className="srp__range"
                  />
                  <div className="srp__range-labels"><span>₹300</span><span>₹3,000</span></div>
                </div>

                <div className="srp__fp-group">
                  <h4 className="srp__fp-title">Care Level</h4>
                  <div className="srp__fp-checks">
                    {CARE_LEVELS.map(l => (
                      <label key={l} className="srp__fp-check">
                        <input type="checkbox" checked={careLevels.includes(l)} onChange={() => toggleCare(l)} />
                        <span className="srp__fp-box" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="srp__fp-group">
                  <h4 className="srp__fp-title">Light Needs</h4>
                  <div className="srp__fp-checks">
                    {['Low light', 'Indirect', 'Bright indirect', 'Direct sun'].map(l => (
                      <label key={l} className="srp__fp-check">
                        <input type="checkbox" readOnly />
                        <span className="srp__fp-box" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>

                {hasActiveFilters && (
                  <button className="srp__fp-clear" onClick={clearFilters}>Clear all</button>
                )}
              </div>
            </div>
          )}

          {/* Grid / states */}
          {loading ? (
            <div className="srp__grid">
              {[...Array(PAGE_SIZE)].map((_, i) => <div key={i} className="srp__skeleton" />)}
            </div>
          ) : error ? (
            <div className="srp__empty">
              <span>⚠️</span>
              <h3>Something went wrong</h3>
              <p>{error}</p>
              <button onClick={fetchProducts}>Try again</button>
            </div>
          ) : allProducts.length === 0 ? (
            <div className="srp__empty">
              <span>🌱</span>
              <h3>No results for "{search}"</h3>
              <p>Check for typos, or try a broader term</p>
              <div className="srp__empty-suggestions">
                {POPULAR_SEARCHES.slice(0, 4).map(t => (
                  <button key={t} className="srp__popular-tag" onClick={() => setInputVal(t)}>{t}</button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="srp__grid">
                {pagedProducts.map(p => (
                  <ProductCard key={p.id || p._id} product={p} addToCart={addToCart} />
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
            </>
          )}
        </div>
      )}
    </main>
  );
}